import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Folder, Plus, Edit2, Trash2, Video, LogOut, User } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_videogist-3/artifacts/809w15is_summbee%20logo_just%20bee.png";

const PROJECT_COLORS = [
  "#FFBF00", // Amber
  "#FF6B6B", // Red
  "#4ECDC4", // Teal
  "#95E1D3", // Mint
  "#F38181", // Pink
  "#AA96DA", // Purple
  "#FCBAD3", // Light Pink
  "#FFFFD2", // Light Yellow
];

function ProjectsPage() {
  const { user, subscription, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [projectName, setProjectName] = useState("");
  const [selectedColor, setSelectedColor] = useState(PROJECT_COLORS[0]);
  const [saving, setSaving] = useState(false);

  const canUseProjects = subscription?.effective_plan && 
    (subscription.effective_plan === "pro" || subscription.effective_plan === "creator" || subscription.in_trial);

  useEffect(() => {
    if (!canUseProjects) {
      navigate("/pricing");
      return;
    }
    fetchProjects();
  }, [canUseProjects]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`, { withCredentials: true });
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error("Error fetching projects:", error);
      if (error.response?.status === 403) {
        navigate("/pricing");
      } else {
        toast.error("Failed to load projects");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!projectName.trim()) {
      toast.error("Please enter a project name");
      return;
    }

    setSaving(true);
    try {
      await axios.post(
        `${API}/projects`,
        { name: projectName, color: selectedColor },
        { withCredentials: true }
      );
      toast.success("Project created!");
      setShowCreateModal(false);
      setProjectName("");
      setSelectedColor(PROJECT_COLORS[0]);
      fetchProjects();
    } catch (error) {
      console.error("Error creating project:", error);
      toast.error(error.response?.data?.detail || "Failed to create project");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateProject = async () => {
    if (!projectName.trim()) {
      toast.error("Please enter a project name");
      return;
    }

    setSaving(true);
    try {
      await axios.put(
        `${API}/projects/${editingProject.id}`,
        { name: projectName, color: selectedColor },
        { withCredentials: true }
      );
      toast.success("Project updated!");
      setEditingProject(null);
      setProjectName("");
      setSelectedColor(PROJECT_COLORS[0]);
      fetchProjects();
    } catch (error) {
      console.error("Error updating project:", error);
      toast.error(error.response?.data?.detail || "Failed to update project");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm("Are you sure you want to delete this project? Videos will not be deleted.")) {
      return;
    }

    try {
      await axios.delete(`${API}/projects/${projectId}`, { withCredentials: true });
      toast.success("Project deleted");
      fetchProjects();
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error(error.response?.data?.detail || "Failed to delete project");
    }
  };

  const openEditModal = (project) => {
    setEditingProject(project);
    setProjectName(project.name);
    setSelectedColor(project.color);
  };

  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="loading-state">Loading...</div>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <nav className="navbar">
        <div className="nav-content">
          <div className="nav-logo" onClick={() => navigate("/")} style={{cursor: "pointer"}}>
            <img src={LOGO_URL} alt="SummBee" className="logo-image" />
            <span>SummBee</span>
          </div>
          <div className="nav-links">
            <Button onClick={() => navigate("/dashboard")} variant="ghost">
              Dashboard
            </Button>
            <Button onClick={() => navigate("/library")} variant="ghost">
              My Videos
            </Button>
            <Button onClick={() => navigate("/projects")} variant="ghost" className="active">
              Projects
            </Button>
            <div className="user-info">
              <User size={16} />
              <span>{user?.name}</span>
            </div>
            <Button onClick={logout} variant="ghost" className="logout-button">
              <LogOut size={16} /> Logout
            </Button>
          </div>
        </div>
      </nav>

      <div className="dashboard-container">
        <div className="page-header">
          <div>
            <h1 className="page-title">Projects</h1>
            <p className="page-subtitle">Organize your videos into projects and topics</p>
          </div>
          <Button onClick={() => setShowCreateModal(true)} className="create-button">
            <Plus size={20} /> New Project
          </Button>
        </div>

        {projects.length === 0 ? (
          <Card className="empty-state-card">
            <CardContent className="empty-state">
              <Folder size={64} className="empty-icon" />
              <h3>No projects yet</h3>
              <p>Create your first project to start organizing videos</p>
              <Button onClick={() => setShowCreateModal(true)} className="create-button">
                <Plus size={20} /> Create Project
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="projects-grid">
            {projects.map((project) => (
              <Card key={project.id} className="project-card">
                <CardHeader>
                  <div className="project-header">
                    <div className="project-info">
                      <div 
                        className="project-color-badge" 
                        style={{ backgroundColor: project.color }}
                      />
                      <CardTitle className="project-name">{project.name}</CardTitle>
                    </div>
                    <div className="project-actions">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditModal(project)}
                      >
                        <Edit2 size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteProject(project.id)}
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="project-stats">
                    <Video size={20} />
                    <span>{project.video_count || 0} videos</span>
                  </div>
                  <Button
                    variant="outline"
                    className="view-videos-button"
                    onClick={() => navigate(`/library?project=${project.id}`)}
                  >
                    View Videos
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingProject) && (
        <div className="modal-overlay">
          <div className="modal-content project-modal">
            <div className="modal-header">
              <h2>{editingProject ? "Edit Project" : "New Project"}</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingProject(null);
                  setProjectName("");
                  setSelectedColor(PROJECT_COLORS[0]);
                }}
                className="close-button"
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Project Name</label>
                <Input
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="e.g., Marketing Videos"
                  maxLength={50}
                />
              </div>
              <div className="form-group">
                <label>Color</label>
                <div className="color-picker">
                  {PROJECT_COLORS.map((color) => (
                    <button
                      key={color}
                      className={`color-option ${selectedColor === color ? "selected" : ""}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setSelectedColor(color)}
                    />
                  ))}
                </div>
              </div>
              <Button
                onClick={editingProject ? handleUpdateProject : handleCreateProject}
                disabled={saving}
                className="save-button"
              >
                {saving ? "Saving..." : editingProject ? "Update Project" : "Create Project"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProjectsPage;
